import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LayoutDashboard, LogOut, ShieldCheck, UserCog } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { AUDIENCE_NAV } from '@/data/audiences';
import LanguageToggle from './LanguageToggle';
import SolutionsMenu from './SolutionsMenu';
import Logo from './brand/Logo';

const Header: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();


  // ---------------------------------------------------------------------------
  // FOUR-AUDIENCE NAVIGATION
  // ---------------------------------------------------------------------------
  // A first-time visitor could previously see only "Find Work · Commercial ·
  // How It Works · Pricing · Contact" — which named two of the four audiences
  // and buried homeowners and contractors below the fold. All four segments now
  // have a top-level path: Homeowners, Commercial, Contractors, Subcontractors.
  //
  // Every audience item resolves to its dedicated, indexable landing page
  // (/homeowners, /commercial, /general-contractors, /subcontractors) — all four
  // already ship in the sitemap with EN/ES hreflang pairs, so exposing them here
  // strengthens internal linking without creating a single new URL to maintain.
  // Labels and destinations come from AUDIENCE_NAV (src/data/audiences.ts), the
  // same list the hero selector reads, so nav and hero cannot disagree.
  //
  // TWO RENDERINGS, ONE LIST:
  //   xl and up, signed out .... flat bar, all four audiences visible at once
  //   md-lg, or signed in ...... "Solutions" menu (the bar would otherwise clip)
  // Both are always in the DOM, so crawlers and screen readers always see the
  // four links regardless of viewport.
  //
  // Role-awareness is preserved: a homeowner account still never sees the
  // contractor lead feed or contractor membership pricing.
  const homeowner = profile?.user_type === 'homeowner' && !profile?.is_admin;

  const utilityLinks = [
    { to: path('/how-it-works'), label: t('nav.howItWorks') },
    ...(homeowner ? [] : [{ to: path('/pricing'), label: t('nav.pricing') }]),
    { to: path('/contact'), label: t('nav.contact') },
  ];

  const audienceLinks = AUDIENCE_NAV.map((a) => ({
    key: a.key,
    to: path(a.to),
    label: t(a.labelKey),
    icon: a.icon,
    accent: a.accent,
    desc: t(a.descKey),
  }));

  const findWork = homeowner ? null : { to: path('/jobs'), label: t('nav.jobs') };

  // Signed-in headers carry a wider right-hand cluster (Profile + Dashboard +
  // Log out), so the flat eight-item bar is reserved for signed-out visitors.
  const flatBar = !profile;

  const handleLogout = async () => {
    await signOut();
    setOpen(false);
    navigate(path('/'));
  };

  // Current-page highlight. Compared against the already-localised href, so it
  // works identically on /en/commercial and /es/comercial.
  const isCurrent = (to: string) => pathname === to || pathname === `${to}/`;

  const linkCls =
    'rounded-lg px-2.5 py-2 text-sm font-semibold transition-colors hover:bg-slate-100 hover:text-slate-900';
  const navCls = (to: string) =>
    `${linkCls} ${isCurrent(to) ? 'bg-slate-100 text-slate-900' : 'text-slate-600'}`;


  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Lockup comes from the shared brand components, so the header, the
            footer, the favicon and the brand studio can never disagree. */}
        <Link to={path('/')} onClick={() => setOpen(false)} aria-label="GIGGONE">
          <Logo tone="light" size={36} />
        </Link>

        {/* FLAT BAR — every audience named, no dropdown required. Always in the
            DOM for crawlers; shown from xl up for signed-out visitors. */}
        <nav
          aria-label={t('nav.primary')}
          className={flatBar ? 'hidden items-center gap-0.5 xl:flex' : 'hidden'}
        >
          {findWork && (
            <Link to={findWork.to} className={navCls(findWork.to)} aria-current={isCurrent(findWork.to) ? 'page' : undefined}>
              {findWork.label}
            </Link>
          )}
          {audienceLinks.map((l) => (
            <Link key={l.key} to={l.to} className={navCls(l.to)} aria-current={isCurrent(l.to) ? 'page' : undefined}>
              {l.label}
            </Link>
          ))}
          <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
          {utilityLinks.map((l) => (
            <Link key={l.to} to={l.to} className={navCls(l.to)} aria-current={isCurrent(l.to) ? 'page' : undefined}>
              {l.label}
            </Link>
          ))}
        </nav>

        {/* COMPACT BAR — md through lg, and every width once signed in. Same four
            audiences, grouped under "Solutions" so the bar never clips. */}
        <nav
          aria-label={t('nav.primary')}
          className={`hidden items-center gap-0.5 md:flex ${flatBar ? 'xl:hidden' : ''}`}
        >
          {findWork && (
            <Link to={findWork.to} className={navCls(findWork.to)} aria-current={isCurrent(findWork.to) ? 'page' : undefined}>
              {findWork.label}
            </Link>
          )}
          <SolutionsMenu />
          {utilityLinks.map((l) => (
            <Link key={l.to} to={l.to} className={navCls(l.to)} aria-current={isCurrent(l.to) ? 'page' : undefined}>
              {l.label}
            </Link>
          ))}
        </nav>



        <div className="hidden items-center gap-3 md:flex">
          <LanguageToggle />
          {profile ? (
            <>
              {profile.is_admin && (
                <Link
                  to={path('/admin')}
                  className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
                >
                  <ShieldCheck className="h-4 w-4" /> {t('nav.admin')}
                </Link>
              )}
              <Link
                to={path('/profile')}
                className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
              >
                <UserCog className="h-4 w-4" /> {lang === 'es' ? 'Perfil' : 'Profile'}
              </Link>
              <Link
                to={path('/dashboard')}
                className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800"
              >
                <LayoutDashboard className="h-4 w-4" /> {t('nav.dashboard')}
              </Link>

              <button
                onClick={handleLogout}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                aria-label={t('nav.logout')}
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <Link to={path('/login')} className="px-3 py-2 text-sm font-semibold text-slate-700 hover:text-slate-900">
                {t('nav.login')}
              </Link>
              <Link
                to={path('/register')}
                className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-orange-600"
              >
                {t('nav.signup')}
              </Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <LanguageToggle compact />
          <button
            onClick={() => setOpen((o) => !o)}
            className="rounded-lg p-2 text-slate-700 hover:bg-slate-100"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white md:hidden">
          <div className="space-y-1 px-4 py-3">
            {/* MOBILE: the four audiences come FIRST and are labelled, so the
                "who is this for?" answer is the first thing in the sheet rather
                than something the visitor has to scroll the home page to find.
                Icons match the hero selector cards exactly. */}
            <p className="px-3 pb-1 pt-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">
              {t('nav.solutions')}
            </p>
            {audienceLinks.map((l) => (
              <Link
                key={l.key}
                to={l.to}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base font-semibold text-slate-700 hover:bg-slate-50"
              >
                <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${l.accent}`}>
                  <l.icon className="h-4 w-4" aria-hidden="true" />
                </span>
                {l.label}
              </Link>
            ))}

            <div className="mt-2 space-y-1 border-t border-slate-100 pt-2">
              {findWork && (
                <Link
                  to={findWork.to}
                  onClick={() => setOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-base font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {findWork.label}
                </Link>
              )}
              {utilityLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="block rounded-lg px-3 py-2.5 text-base font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {l.label}
                </Link>
              ))}
            </div>

            <div className="mt-2 grid gap-2 border-t border-slate-100 pt-3">
              {profile ? (
                <>
                  {profile.is_admin && (
                    <Link
                      to={path('/admin')}
                      onClick={() => setOpen(false)}
                      className="rounded-lg border border-slate-200 px-3 py-2.5 text-center text-base font-bold text-slate-800"
                    >
                      {t('nav.admin')}
                    </Link>
                  )}
                  <Link
                    to={path('/profile')}
                    onClick={() => setOpen(false)}
                    className="rounded-lg border border-slate-200 px-3 py-2.5 text-center text-base font-bold text-slate-800"
                  >
                    {lang === 'es' ? 'Configuración de Perfil' : 'Profile Settings'}
                  </Link>
                  <Link
                    to={path('/dashboard')}
                    onClick={() => setOpen(false)}
                    className="rounded-lg bg-slate-900 px-3 py-2.5 text-center text-base font-bold text-white"
                  >
                    {t('nav.dashboard')}
                  </Link>

                  <button
                    onClick={handleLogout}
                    className="rounded-lg border border-slate-200 px-3 py-2.5 text-base font-bold text-slate-700"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to={path('/login')}
                    onClick={() => setOpen(false)}
                    className="rounded-lg border border-slate-200 px-3 py-2.5 text-center text-base font-bold text-slate-800"
                  >
                    {t('nav.login')}
                  </Link>
                  <Link
                    to={path('/register')}
                    onClick={() => setOpen(false)}
                    className="rounded-lg bg-orange-500 px-3 py-2.5 text-center text-base font-bold text-white"
                  >
                    {t('nav.signup')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
